import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { graphql, useStaticQuery } from 'gatsby'

import { strapiPageToLinkTo } from '@utils/cms/pageLink'
import useStaticContentful from './useStaticContentful'

type StrapiPageLink = {
  label?: string | null
  slug?: string | null
  path?: string | null
  url?: string | null
  metaType?: string | null
  titleInternal?: string | null
}

type StrapiNavComponent = {
  entry?: StrapiPageLink | null
}

const fallbackLink: MCDC.Props.ILinkTo = {
  label: 'Home',
  url: '/',
  isExternal: false,
}

const useStrapiNavigation = () => {
  const { locale } = useIntl()
  const { meta } = useStaticContentful()
  const { navigation, account } = useStaticQuery(
    graphql`
      query StrapiNavigationQuery {
        navigation: strapiNavigation {
          identifier
          homePage {
            label
            slug
            path
            metaType
            titleInternal
          }
          mainPages {
            entry {
              label
              slug
              path
              metaType
              titleInternal
            }
          }
          metaPages {
            ... on STRAPI__COMPONENT_LINK_PAGE_EXTERNAL {
              entry {
                label
                url
                titleInternal
              }
            }
            ... on STRAPI__COMPONENT_LINK_PAGE_SEO {
              entry {
                label
                slug
                titleInternal
              }
            }
            ... on STRAPI__COMPONENT_LINK_PAGE_GENERIC {
              entry {
                label
                slug
                path
                metaType
                titleInternal
              }
            }
          }
        }
        account: allLocalPageAccount {
          nodes {
            ...PageAccountFragment
          }
        }
      }
    `
  )

  return useMemo(() => {
    if (!navigation) {
      return {
        homePage: fallbackLink,
        accountPage: fallbackLink,
        mainPages: [] as MCDC.Props.ILinkTo[],
        metaPages: [] as MCDC.Props.ILinkTo[],
      }
    }

    const accountPage = account.nodes.find(
      (entry: MCDC.Contentful.IPageAccount) => entry.node_locale === locale
    )

    const mapNavEntry = (item: StrapiNavComponent): MCDC.Props.ILinkTo =>
      strapiPageToLinkTo(item?.entry, meta)

    return {
      homePage: strapiPageToLinkTo(navigation.homePage, meta),
      accountPage: accountPage?.fields?.linkTo ?? fallbackLink,
      mainPages: (navigation.mainPages || []).map(mapNavEntry),
      metaPages: (navigation.metaPages || []).map(mapNavEntry),
    }
  }, [navigation, locale, meta, account])
}

export default useStrapiNavigation
